import { Box, Button, Flex } from 'components/ui';
import { useState } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { futuraChainId, futuraContractAddress } from 'utils/config';
import DotsLoader from './DotsLoader';
import { fetchOwnedNft } from 'utils/platformPublicApi';
import { OwnedNft } from 'utils/types'

// The outer container that wraps the input and the button.
const FilterOuterContainer = styled(Flex)`
  align-items: center;
  height: 28px;
  position: relative;
`;

interface FilterImageButtonProps {
  opacity: number;
}

// The button that will allow the user the navigate to a specific token.
const FilterImageButton = styled(Button)<FilterImageButtonProps>`
  height: 100%;
  width: 75px;
  background: black;
  box-shadow: 0 0 5px 0 black;
  border-radius: 0;
  padding: 5px;
  display: flex;
  margin-left: 5px;
  color: white;

  &:focus,
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 5px 0 white;
  }
`;

// The numeric input that the user can use to fill in a token ID.
const TokenIDInput = styled.input`
  background: black;
  border: 1px solid black;
  box-shadow: 0 0 5px 0 black;
  color: white;
  height: 100%;
  width: 130px;
  padding-left: 10px;

  ::placeholder {
    color: grey;
    font-style: italic;
    font-size: 12px;
  }

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }

  :focus::placeholder {
    color: transparent;
  }

  &:focus {
    outline: none;
  }
`;

interface EvaluationFeedbackContainerProps {
  canNavigate: boolean;
}

const EvaluationFeedbackContainer = styled.div<EvaluationFeedbackContainerProps>`
  position: absolute;
  top: 25px;
  left: 10px;
  font-style: italic;
  font-weight: bold;
  font-size: 9.5px;
  width: 100%;
  text-shadow: 2px 2px black;
  color: ${props => (props.canNavigate ? 'limegreen' : 'orange')};
`;

// Fetches a token for a token ID in a debounced way.
const fetchTokenDebounced = debounce(
  async ({
    tokenID,
    setLoading,
    setCanNavigate,
  }: {
    tokenID: any;
    setLoading: React.Dispatch<boolean>;
    setCanNavigate: React.Dispatch<boolean>;
  }) => {
    // Start loading feedback for user.
    setLoading(true);

    // When we perform a new GET, we disable the navigation until we know the new result.
    setCanNavigate(false);

    try {
      // Perform a GET for a token with the given token ID.
      const result: OwnedNft | undefined = await fetchOwnedNft(
        futuraContractAddress,
        tokenID,
        futuraChainId
      );

      // Only update navigation if the token exists.
      if (result?.metadata) {
        setCanNavigate(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Always stop user loading feedback when the request has finished.
      setLoading(false);
    }
  },
  500
);

const FuturaGalleryTokenIDFilter = () => {
  // Keeps track of whether we are evaluating the input of the user.
  const [loading, setLoading] = useState(false);

  // Keeps track of the active token ID.
  const [tokenId, setTokenId] = useState<number>();

  // Keeps track of whether or not we can navigate to the token details page for the active token ID.
  const [canNavigate, setCanNavigate] = useState(false);

  // Opens a new tab to the details of that token ID.
  function navigateToTokenDetails() {
    if (tokenId) {
      const url = `details/${tokenId}`;
      if (url) window.open(url, '_self');
    }
  }

  // When the user has entered a new token ID, we need to reflect that in our state.
  const handleTokenIdChange = (e: any) => {
    // Sync changes to state.
    const { value } = e.target;
    setTokenId(value);

    // Fetch the associated token.opacity: loading || canNavigate ? '1' : '0.5'
    fetchTokenDebounced({ tokenID: value, setLoading, setCanNavigate });
  };

  // When a user presses the enter key and there's a token ID in our state, navigate to the token details page.
  const handleTokenIdKeypress = (event: any) => {
    if (event.key && event.key.toLowerCase() === 'enter' && canNavigate) {
      navigateToTokenDetails();
    }
  };

  return (
    <FilterOuterContainer>
      <TokenIDInput
        id="tokenID"
        type="number"
        placeholder={!loading ? 'Enter Mix Token ID' : ''}
        value={tokenId}
        onChange={handleTokenIdChange}
        onKeyPress={handleTokenIdKeypress}
      />

      <FilterImageButton
        disabled={!canNavigate || loading}
        opacity={loading || canNavigate ? '1' : '0.55'}
        title="Click to navigate to a specific token by its ID."
        onClick={navigateToTokenDetails}
      >
        {loading ? (
          <DotsLoader height={10} width={15} dotBackgroundColor="white" />
        ) : (
          <Box marginLeft={1}>Load</Box>
        )}
      </FilterImageButton>

      {tokenId && !loading && (
        <EvaluationFeedbackContainer canNavigate={canNavigate}>
          {canNavigate ? 'Token found.' : 'No token found.'}
        </EvaluationFeedbackContainer>
      )}
    </FilterOuterContainer>
  );
};

export default FuturaGalleryTokenIDFilter;
